import type { UseFormGetValues, UseFormSetValue, UseFormUnregister } from 'react-hook-form';

import type { ContractMethodFormFields } from './utils';

export const MAX_DEFAULT_FIXED_ADDRESS_ARRAY_INPUTS = 5;

export function getInitialAddressArrayRowCount(hasFixedSize: boolean, maxSize: number): number {
  if (!hasFixedSize) {
    return 1;
  }

  return Math.min(maxSize, MAX_DEFAULT_FIXED_ADDRESS_ARRAY_INPUTS);
}

export function getInitialAddressArrayIndices(hasFixedSize: boolean, maxSize: number): Array<number> {
  const count = getInitialAddressArrayRowCount(hasFixedSize, maxSize);
  return Array.from({ length: count }, (_, index) => index);
}

export function getExistingAddressArrayValues(
  basePath: string,
  registeredIndices: Array<number>,
  getValues: UseFormGetValues<ContractMethodFormFields>,
): Array<string> {
  return registeredIndices
    .map((_, index) => {
      const value = getValues(`${ basePath }:${ index }`);
      return typeof value === 'string' ? value.trim() : '';
    })
    .filter((value) => value !== '');
}

export function getLimitedBatchAddresses(
  existingAddresses: Array<string>,
  newAddresses: Array<string>,
  hasFixedSize: boolean,
  maxSize: number,
): Array<string> {
  const combinedAddresses = [ ...existingAddresses, ...newAddresses ];
  return hasFixedSize ? combinedAddresses.slice(0, maxSize) : combinedAddresses;
}

export function getNextRegisteredIndices(prev: Array<number>, nextCount: number): Array<number> {
  const startId = prev.length > 0 ? Math.max(...prev) + 1 : 0;
  return Array.from({ length: nextCount }, (_, index) => startId + index);
}

interface ApplyBatchAddressesToFormParams {
  basePath: string;
  registeredIndices: Array<number>;
  addresses: Array<string>;
  hasFixedSize: boolean;
  maxSize: number;
  getValues: UseFormGetValues<ContractMethodFormFields>;
  setValue: UseFormSetValue<ContractMethodFormFields>;
  unregister: UseFormUnregister<ContractMethodFormFields>;
}

export function applyBatchAddressesToForm({
  basePath,
  registeredIndices,
  addresses,
  hasFixedSize,
  maxSize,
  getValues,
  setValue,
  unregister,
}: ApplyBatchAddressesToFormParams): Array<number> {
  const existingAddresses = getExistingAddressArrayValues(basePath, registeredIndices, getValues);
  const limitedAddresses = getLimitedBatchAddresses(existingAddresses, addresses, hasFixedSize, maxSize);
  const minCount = getInitialAddressArrayRowCount(hasFixedSize, maxSize);
  const nextCount = Math.max(limitedAddresses.length, minCount);

  registeredIndices.forEach((_, index) => {
    if (index >= nextCount) {
      unregister(`${ basePath }:${ index }`);
    }
  });

  limitedAddresses.forEach((address, index) => {
    setValue(`${ basePath }:${ index }`, address, { shouldValidate: true });
  });

  for (let index = limitedAddresses.length; index < nextCount; index++) {
    setValue(`${ basePath }:${ index }`, '', { shouldValidate: true });
  }

  return getNextRegisteredIndices(registeredIndices, nextCount);
}
